import React, { useState, useEffect } from 'react';
import { Input, Form, Message, Radio, Modal } from '@b-design/fusion';
import Button, { FormSubmit, promisifyValidate } from '@/components/Button';
import $i18n from '@/i18n/index';
import Requests from '@/services/request';
import Uploader from '@/components/Uploader';
import appLogo from '@/assets/app-logo.png';
import { LOGO } from '@/utils/config';
import link from '@/assets/link.png';
import { Field } from '@alifd/next';
import store from '@/store';
import './index.less';

const SiteSetting: React.FC = () => {
  const field = Field.useField();
  const { init, setValue, getError, setError } = field;
  const [brandLogo, setBrandLogo] = useState<any>({ publicUrl: '', relativePath: '' });
  const [browserIcon, setBrowserIcon] = useState<any>({ publicUrl: '', relativePath: '' });
  const [defaultIcon, setDefaultIcon] = useState<any>({ publicUrl: '', relativePath: '' });
  const [siteLanguage, setSiteLanguage] = useState('en');
  const [copyrightLanguage, setCopyrightLanguage] = useState('en');
  const [zhSiteValue, setZhSiteValue] = useState('');
  const [enSiteValue, setEnSiteValue] = useState('');
  const [zhCopyrightValue, setZhCopyrightValue] = useState('');
  const [enCopyrightValue, setEnCopyrightValue] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [siteSettingsState] = store.useModel('siteSettings');

  useEffect(() => {
    setEnSiteValue(siteSettingsState?.name?.en_US);
    setZhSiteValue(siteSettingsState?.name?.zh_CN);
    setEnCopyrightValue(siteSettingsState?.copyright?.en_US);
    setZhCopyrightValue(siteSettingsState?.copyright?.zh_CN);
    setTimeout(() => {
      setValue('siteName', siteSettingsState?.name?.en_US);
      setValue('copyright', siteSettingsState?.copyright?.en_US);
    }, 100);
    if (siteSettingsState?.logo) {
      setBrandLogo({ publicUrl: siteSettingsState?.logo, relativePath: siteSettingsState?.logo });
    }
    if (siteSettingsState?.icon) {
      setBrowserIcon({ publicUrl: siteSettingsState?.icon, relativePath: siteSettingsState?.icon });
    }
    if (siteSettingsState?.miniappDefaultIcon) {
      setDefaultIcon({
        publicUrl: siteSettingsState?.miniappDefaultIcon,
        relativePath: siteSettingsState?.miniappDefaultRelativeIcon,
      });
    }
  }, []);

  useEffect(() => {
    if (siteLanguage === 'en') {
      setValue('siteName', enSiteValue);
    } else {
      setValue('siteName', zhSiteValue);
    }
    if (copyrightLanguage === 'en') {
      setValue('copyright', enCopyrightValue);
    } else {
      setValue('copyright', zhCopyrightValue);
    }
  }, [siteLanguage, copyrightLanguage]);

  const submitModal = async () => {
    const [err, value] = await promisifyValidate(field);
    // 必填英文
    if (!enSiteValue) {
      setError(
        'siteName',
        $i18n.get({
          id: 'aopf.component.chooseChannelModal.PleaseEnter',
          dm: '请输入',
        }),
      );
    }
    if (!enCopyrightValue) {
      setError(
        'copyright',
        $i18n.get({
          id: 'aopf.component.chooseChannelModal.PleaseEnter',
          dm: '请输入',
        }),
      );
    }
    const param = {
      name: {
        en_US: enSiteValue,
        zh_CN: zhSiteValue ? zhSiteValue : enSiteValue,
      },
      copyright: {
        en_US: enCopyrightValue,
        zh_CN: zhCopyrightValue ? zhCopyrightValue : enCopyrightValue,
      },
      ...(brandLogo?.publicUrl !== '' && { logo: brandLogo?.publicUrl }),
      ...(browserIcon?.publicUrl && { icon: browserIcon?.publicUrl }),
      ...(defaultIcon?.publicUrl && {
        miniappDefaultIcon: defaultIcon?.publicUrl,
        miniappDefaultRelativeIcon: defaultIcon?.relativePath,
      }),
    };
    if (enSiteValue && enCopyrightValue) {
      const data = await Requests.post('/rest/intercnn/tenant/config?type=SITE', { ...param });
      if (data) {
        Message.success({
          title: $i18n.get({
            id: 'aopf.pages.settings.set.successfully',
            dm: '设置成功',
          }),
        });
        setIsModalVisible(true);
      }
    }
  };

  const changeSiteLanguage = (v) => {
    setSiteLanguage(v);
    if (v === 'en') {
      setValue('siteName', enSiteValue);
    } else {
      setValue('siteName', zhSiteValue);
    }
  };

  const changeCopyrightLanguage = (v) => {
    setCopyrightLanguage(v);
    if (v === 'en') {
      setValue('copyright', enCopyrightValue);
    } else {
      setValue('copyright', zhCopyrightValue);
    }
  };

  const siteLanguageRadio = (
    <Radio.Group
      shape="button"
      data-shape="button"
      onChange={changeSiteLanguage}
      defaultValue={siteLanguage}
    >
      <Radio id="en" value="en">
        {$i18n.get({
          id: 'aopf.pages.settings.en',
          dm: '英文',
        })}
      </Radio>
      <Radio id="zh" value="zh">
        {$i18n.get({
          id: 'aopf.pages.settings.zh',
          dm: '中文',
        })}
      </Radio>
    </Radio.Group>
  );

  const copyrightLanguageRadio = (
    <Radio.Group
      shape="button"
      data-shape="button"
      onChange={changeCopyrightLanguage}
      defaultValue={copyrightLanguage}
    >
      <Radio id="en" value="en">
        {$i18n.get({
          id: 'aopf.pages.settings.en',
          dm: '英文',
        })}
      </Radio>
      <Radio id="zh" value="zh">
        {$i18n.get({
          id: 'aopf.pages.settings.zh',
          dm: '中文',
        })}
      </Radio>
    </Radio.Group>
  );

  const changeSiteValue = (e) => {
    if (siteLanguage === 'zh') {
      setZhSiteValue(e.target.value);
    } else {
      setEnSiteValue(e.target.value);
    }
  };

  const changeCopyrightValue = (e) => {
    if (copyrightLanguage === 'zh') {
      setZhCopyrightValue(e.target.value);
    } else {
      setEnCopyrightValue(e.target.value);
    }
  };

  return (
    <div className="settingContent">
      <div className="title">
        {$i18n.get({
          id: 'aopf.pages.settings.site',
          dm: '网站设置',
        })}
      </div>
      <div>
        <Form>
          <Form.Item
            name="siteName"
            label={$i18n.get({
              id: 'aopf.pages.settings.site.name',
              dm: '站点名称',
            })}
            required
          >
            {siteLanguage === 'en' ? (
              <Input
                addonBefore={siteLanguageRadio}
                onBlur={changeSiteValue}
                style={{ width: '640px' }}
                showLimitHint
                maxLength={40}
                {...init('siteName', {
                  initValue: $i18n.get({
                    id: 'aopf.components.site.ChineseApplicationOpenPlatform',
                    dm: 'Application Open Platform',
                  }),
                })}
              />
            ) : (
              <Input
                addonBefore={siteLanguageRadio}
                onBlur={changeSiteValue}
                style={{ width: '640px' }}
                showLimitHint
                maxLength={16}
                {...init('siteName', {
                  initValue: $i18n.get({
                    id: 'aopf.components.site.OpenApplicationPlatform',
                    dm: '应用程序开放平台',
                  }),
                })}
              />
            )}

            <p className="form-create-err-msg-p">{getError('siteName')}</p>
          </Form.Item>

          <Form.Item
            name="brandLogo"
            label={$i18n.get({ id: 'aopf.pages.settings.brand.logo', dm: '品牌标志' })}
            className="upload"
          >
            <Uploader.Image
              {...init('brandLogo', {})}
              action="/rest/intercnn/uploadPublicRead"
              accept={['image/png', 'image/jpg', 'image/jpeg']}
              key={brandLogo?.relativePath}
              value={
                brandLogo?.relativePath
                  ? [
                      {
                        uid: '0',
                        state: 'done',
                        url: brandLogo?.publicUrl,
                        downloadURL: brandLogo?.publicUrl,
                        imgURL: brandLogo?.publicUrl,
                        relativePath: brandLogo?.relativePath,
                        publicUrl: brandLogo?.publicUrl,
                      },
                    ]
                  : []
              }
              maxSize={1024 * 1024}
              onCheckFail={(_err, response) => {
                Message.warning({ title: response.msg });
              }}
              onError={() => {
                Message.error({
                  title: $i18n.get({
                    id: 'aopf.pages.miniapp.drawer.form.icon.Upload.failure',
                    dm: '上传失败',
                  }),
                });
              }}
              changeValue={setBrandLogo}
              defaultImg={LOGO}
            />

            <div className="upload-format">
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format1',
                dm: '文件类型:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format1.content',
                  dm: '.png / .jpg / .jpeg',
                })}
              </span>
              <br />
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format2',
                dm: '规格:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format2.content',
                  dm: '180px * 180px',
                })}
              </span>
              <br />
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format3',
                dm: '最大大小:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format3.other.content',
                  dm: '120KB',
                })}
              </span>
              <br />
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format4',
                dm: '背景:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format4.content',
                  dm: '不透明',
                })}
              </span>
            </div>
          </Form.Item>

          <Form.Item
            name="browserIcon"
            label={$i18n.get({ id: 'aopf.pages.settings.browser.icon', dm: '浏览器图标' })}
            className="upload"
          >
            <Uploader.Image
              {...init('browserIcon', {})}
              action="/rest/intercnn/uploadPublicRead"
              accept={['image/png', 'image/jpg', 'image/jpeg']}
              key={browserIcon?.relativePath}
              value={
                browserIcon?.relativePath
                  ? [
                      {
                        uid: '0',
                        state: 'done',
                        url: browserIcon?.publicUrl,
                        downloadURL: browserIcon?.publicUrl,
                        imgURL: browserIcon?.publicUrl,
                        relativePath: browserIcon?.relativePath,
                        publicUrl: browserIcon?.publicUrl,
                      },
                    ]
                  : []
              }
              maxSize={1024 * 1024}
              onCheckFail={(_err, response) => {
                Message.warning({ title: response.msg });
              }}
              onError={() => {
                Message.error({
                  title: $i18n.get({
                    id: 'aopf.pages.miniapp.drawer.form.icon.Upload.failure',
                    dm: '上传失败',
                  }),
                });
              }}
              changeValue={setBrowserIcon}
              defaultImg={link}
            />

            <div className="upload-format">
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format1',
                dm: '文件类型:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format1.content',
                  dm: '.png / .jpg / .jpeg',
                })}
              </span>
              <br />
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format2',
                dm: '规格:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format2.content',
                  dm: '180px * 180px',
                })}
              </span>
              <br />
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format3',
                dm: '最大大小:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format3.other.content',
                  dm: '120KB',
                })}
              </span>
              <br />
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format4',
                dm: '背景:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format4.content',
                  dm: '不透明',
                })}
              </span>
            </div>
          </Form.Item>

          <Form.Item
            name="copyright"
            label={$i18n.get({
              id: 'aopf.pages.settings.Copyright',
              dm: '版权',
            })}
            required
          >
            <Input
              style={{ width: '640px' }}
              addonBefore={copyrightLanguageRadio}
              onBlur={changeCopyrightValue}
              showLimitHint
              maxLength={100}
              {...init('copyright', {
                initValue: $i18n.get({
                  id: 'aopf.components.site.ChineseCopyrightAlibabaCloudAll',
                  dm: 'Copyright © 2022 Alibaba Cloud All rights reserved',
                }),
              })}
            />

            <p className="form-create-err-msg-p">{getError('copyright')}</p>
          </Form.Item>
          <Form.Item
            name="miniappDefaultIcon"
            label={$i18n.get({
              id: 'aopf.pages.settings.Miniapp.default.icon',
              dm: 'Miniapp默认图标',
            })}
            className="upload"
          >
            <Uploader.Image
              {...init('miniappDefaultIcon', {})}
              action="/rest/intercnn/cdn/upload"
              accept={['image/png', 'image/jpg', 'image/jpeg']}
              key={defaultIcon?.relativePath}
              value={
                defaultIcon?.relativePath
                  ? [
                      {
                        uid: '0',
                        state: 'done',
                        url: defaultIcon?.publicUrl,
                        downloadURL: defaultIcon?.publicUrl,
                        imgURL: defaultIcon?.publicUrl,
                        relativePath: defaultIcon?.relativePath,
                        publicUrl: defaultIcon?.publicUrl,
                      },
                    ]
                  : []
              }
              maxSize={1024 * 1024}
              onCheckFail={(_err, response) => {
                Message.warning({ title: response.msg });
              }}
              onError={() => {
                Message.error({
                  title: $i18n.get({
                    id: 'aopf.pages.miniapp.drawer.form.icon.Upload.failure',
                    dm: '上传失败',
                  }),
                });
              }}
              changeValue={setDefaultIcon}
              defaultImg={appLogo}
            />

            <div className="upload-format">
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format1',
                dm: '文件类型:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format1.content',
                  dm: '.png / .jpg / .jpeg',
                })}
              </span>
              <br />
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format2',
                dm: '规格:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format2.content',
                  dm: '180px * 180px',
                })}
              </span>
              <br />
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format3',
                dm: '最大大小:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format3.other.content',
                  dm: '120KB',
                })}
              </span>
              <br />
              {$i18n.get({
                id: 'aopf.pages.miniapp.drawer.form.upload.img.format4',
                dm: '背景:',
              })}
              <span>
                {$i18n.get({
                  id: 'aopf.pages.miniapp.drawer.form.upload.img.format4.content',
                  dm: '不透明',
                })}
              </span>
            </div>
          </Form.Item>
          <FormSubmit
            type="primary"
            onClick={submitModal}
            validate
            style={{
              height: 40,
              fontWeight: 500,
              fontSize: 16,
              color: '#fff',
              letterSpacing: '0.15px',
              marginTop: 40,
            }}
          >
            {$i18n.get({ id: 'aopf.pages.miniapp.version.info.confirm.Ok', dm: '确定' })}
          </FormSubmit>
        </Form>
      </div>
      <Modal
        title={$i18n.get({ id: 'aopf.pages.settings.submit.title', dm: '确认设置' })}
        data-size="xs"
        visible={isModalVisible}
        closeMode={['close', 'esc', 'mask']}
        onClose={() => setIsModalVisible(false)}
        footer={[
          <div className="dialog-footer">
            <Button className="dialog-btn" type="normal" onClick={() => setIsModalVisible(false)}>
              {$i18n.get({ id: 'aopf.components.Confirm.Cancel', dm: '取消' })}
            </Button>
            <Button type="primary" onClick={() => location.reload()}>
              {$i18n.get({ id: 'aopf.components.Confirm.Ok', dm: '确定' })}
            </Button>
          </div>,
        ]}
      >
        {$i18n.get({ id: 'aopf.pages.settings.submitDes', dm: '站点设置编辑成功，刷新页面生效' })}
      </Modal>
    </div>
  );
};
export default SiteSetting;
